/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {catchError, first, map, of, Subscription, tap} from 'rxjs';
import {MapToolsService} from '../../../../services/map-tools.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {FeatureApplyUpdateService} from '../../../../services/feature-apply-update.service';
import {AngleCreationService} from '../../../../services/features/angle/angle-creation.service';
import {PointConstructionToolService} from './point-construction-tool.service';
import {LineService} from '../../../../services/features/line.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {WidgetService} from '../../../../services/widget.service';
import {PlanModelService} from '../../../../../common/service/plan-model.service';
import {switchMap} from 'rxjs/operators';
import {FeaturesDeletionService} from '../../../../services/features-deletion.service';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class DissectLineConstructionToolService implements ToolServiceInterface {
    title = 'DissectLineConstructionToolService';

    attributes: any;

    private lines = [];

    private polygons = [];

    private allAngles = [];

    constructor(
        private angleCreationService: AngleCreationService,
        private constructionToolModelService: ConstructionToolModelService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private featuresDeletionService: FeaturesDeletionService,
        private lookupService: LookupService,
        private lineService: LineService,
        private mapToolsService: MapToolsService,
        private planModelService: PlanModelService,
        private pointConstructionToolService: PointConstructionToolService,
        private spatialUtilService: SpatialUtilService,
        private widgetService: WidgetService
    ) {}

    create(attributes: any) {
        console.debug(`${this.title} : create`);
        let subscription: Subscription;

        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_DISSECT_LINE', () => {
            this.attributes = attributes;

            this.lines = this.spatialUtilService.getLines();
            this.polygons = this.spatialUtilService.getPolygons();
            this.allAngles = this.spatialUtilService.getAngles().filter((angle: any) => !angle.attributes.enlargementid);

            const selectedLines = this.spatialUtilService.getHighlightedLines();
            const selectedPoints = this.spatialUtilService.getHighlightedPoints();

            const valid = this.validate(selectedLines, selectedPoints);
            if (valid) {
                subscription = this.createDissectLine(selectedLines, selectedPoints)
                    .pipe(
                        first(),
                        tap(() => {
                            window.setTimeout(() => {
                                if (this.widgetService.isWidgetActive('IdentifyFeatures')) {
                                    this.eventBus.cast('resetFeaturesInteracted');
                                }
                            }, 200);
                        })
                    )
                    .subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    private getLineTypeLookups(): LineTypeLookup[] {
        return this.lookupService.getLookups('lineType');
    }

    private getTool() {
        return this.constructionToolModelService.getTool('dissectLine');
    }

    private validate(selectedLines: any[], selectedPoints: any[]) {
        console.debug(`${this.title} - validate`);

        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;
        tool.information.length = 0;

        if (selectedLines.length === 0) {
            tool.errors.push(tool.errorMessage1);
        } else if (selectedLines.length >= 2) {
            tool.errors.push(tool.errorMessage2);
        } else if (selectedPoints.length === 0) {
            tool.errors.push(tool.errorMessage3);
        } else if (selectedPoints.length >= 2) {
            tool.errors.push(tool.errorMessage4);
        } else {
            const selectedLine = selectedLines[0];
            const selectedLineLookup = this.getLineTypeLookups().find(l => selectedLine.attributes.type === l.id);
            if (!selectedLineLookup.permitLineDissection) {
                const errorMessage = tool.errorMessage8
                    .replace('<insert LineType Name>', selectedLineLookup.description)
                    .replace('<insert FeatureType>', selectedLineLookup.featureType);
                tool.errors.push(errorMessage);
                return false;
            }

            const pointId = parseInt(selectedPoints[0].attributes.id);

            if (parseInt(selectedLines[0].attributes.frompointid) !== pointId && parseInt(selectedLines[0].attributes.topointid) !== pointId) {
                tool.errors.push(tool.errorMessage5);
            } else {
                // Check that the entered offset distance is less than the geometry distance of the line being dissected:
                const distance = Number(this.attributes.distance);
                const observedDistance = Number(selectedLines[0].attributes.distance.toFixed(3));

                if (distance >= observedDistance) {
                    tool.errors.push(tool.errorMessage7);
                } else {
                    // If the offset distance >= the geometry distance of the line being dissected then present the following message: "The selected
                    // line could not be dissected. The offset distance must be less than the geometry line distance."
                    const fromPoint = new this.esriModuleService.geometry.Point(selectedLines[0].geometry.paths[0][0]);
                    fromPoint.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

                    const toPoint = new this.esriModuleService.geometry.Point(selectedLines[0].geometry.paths[0][1]);
                    toPoint.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

                    const geometryDistance = this.lineService.calcDistance(fromPoint, toPoint);
                    if (distance >= geometryDistance) {
                        tool.errors.push(tool.errorMessage6);
                    } else {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    private createDissectLine(selectedLines: any[], selectedPoints: any[]) {
        console.debug(`${this.title} - createDissectLine`);

        const fromPoint = new this.esriModuleService.geometry.Point(selectedLines[0].geometry.paths[0][0]);
        fromPoint.spatialReference = new this.esriModuleService.SpatialReference(selectedLines[0].geometry.spatialReference);

        const toPoint = new this.esriModuleService.geometry.Point(selectedLines[0].geometry.paths[0][1]);
        toPoint.spatialReference = new this.esriModuleService.SpatialReference(selectedLines[0].geometry.spatialReference);

        const distance = this.lineService.calcDistance(fromPoint, toPoint);
        const distanceRatio = Number(this.attributes.distance) / distance;
        const pointOffset = {x: distanceRatio * (toPoint.x - fromPoint.x), y: distanceRatio * (toPoint.y - fromPoint.y)};

        if (parseInt(selectedLines[0].attributes.frompointid) === parseInt(selectedPoints[0].attributes.id)) {
            this.attributes.x = selectedPoints[0].geometry.x + pointOffset.x;
            this.attributes.y = selectedPoints[0].geometry.y + pointOffset.y;
        } else {
            this.attributes.x = selectedPoints[0].geometry.x - pointOffset.x;
            this.attributes.y = selectedPoints[0].geometry.y - pointOffset.y;
        }

        const selectedLineAngles = this.getSelectedLineAngles(selectedLines[0]);

        return this.pointConstructionToolService
            .createPoint(this.attributes)
            .pipe(switchMap((createdPoint: any) => this.setupDissectedLines(selectedLineAngles, selectedLines, selectedPoints, createdPoint[0])));
    }

    private updateTotalLotBoundaries(selectedLine: any, newLine: any) {
        const selectedLineId = selectedLine.attributes.id;
        const newLineId = newLine.attributes.id;

        const tlbUpdates = this.spatialUtilService
            .getLines()
            .filter((line: any) => this.spatialUtilService.parseIdsAttr(line.attributes.lineids).includes(selectedLineId))
            .map((tlb: any) => {
                const lineIds = this.spatialUtilService.parseIdsAttr(tlb.attributes.lineids);
                lineIds.splice(lineIds.indexOf(selectedLineId), 0, newLineId);
                tlb.attributes.lineids = this.spatialUtilService.toIdsAttr(lineIds);
                return tlb;
            });

        return tlbUpdates.length ? this.spatialUtilService.applyEdits('line', null, tlbUpdates, null) : of(null);
    }

    private setupDissectedLines(selectedLineAngles: any[], selectedLines: any[], selectedPoints: any[], newPoint: any) {
        console.debug(`${this.title} - setupDissectedLines`);

        const selectedLine = selectedLines[0];
        const selectedPoint = selectedPoints[0];
        const lineBeforeUpdate = new this.esriModuleService.Graphic(selectedLine.toJson());
        const anglesBeforeUpdate = selectedLineAngles.map((angle: any) => new this.esriModuleService.Graphic(angle.toJson()));

        return this.updateSelectedLine(selectedLine, selectedPoint, newPoint).pipe(
            switchMap((updatedLine: any) => {
                if (!updatedLine) {
                    return this.rollback(newPoint, undefined, undefined);
                }

                //DCX-8238 Recreate annotations to ensure correct placement on enlargements
                this.eventBus.cast('deleteAnnotations', {featureLayer: this.spatialUtilService.getLineMapLayer(), features: [updatedLine]});
                this.eventBus.cast('createAnnotation', {feature: updatedLine, featureType: 'line'});
                return this.createDissectedLine(lineBeforeUpdate, selectedPoint, newPoint).pipe(
                    switchMap((linesCreated: any[]) => {
                        if (!linesCreated?.length) {
                            return this.rollback(newPoint, lineBeforeUpdate, undefined);
                        }

                        const lineCreated = linesCreated[0];

                        return this.updateTotalLotBoundaries(lineBeforeUpdate, lineCreated).pipe(
                            switchMap(() =>
                                this.updateSelectedLineAngles(selectedLineAngles, selectedPoint, lineBeforeUpdate, lineCreated).pipe(
                                    switchMap((updatedAngles: any[]) => {
                                        if (!updatedAngles) {
                                            return this.rollback(newPoint, lineBeforeUpdate, undefined);
                                        }

                                        return this.createDissectedLineAngles(newPoint, updatedLine, lineCreated).pipe(
                                            switchMap((newAngles: any[]) => {
                                                if (!newAngles?.length) {
                                                    return this.rollback(newPoint, lineBeforeUpdate, anglesBeforeUpdate);
                                                }

                                                return this.updateAssociatedPolygons(lineBeforeUpdate, newPoint, lineCreated).pipe(
                                                    switchMap((updatedPolygons: any[]) => {
                                                        if (updatedPolygons) {
                                                            return of(null);
                                                        }
                                                        return this.rollback(newPoint, lineBeforeUpdate, anglesBeforeUpdate);
                                                    })
                                                );
                                            })
                                        );
                                    })
                                )
                            )
                        );
                    })
                );
            })
        );
    }

    private updateSelectedLine(selectedLine: any, selectedPoint: any, newPoint: any) {
        const logPrefix = `${this.title} - updateSelectedLine`;
        console.debug(logPrefix);

        const selectedLineCopy = new this.esriModuleService.Graphic(selectedLine.toJson());
        selectedLineCopy.attributes.distance = parseFloat(this.attributes.distance.toFixed(3));

        if (parseInt(selectedLineCopy.attributes.frompointid) === parseInt(selectedPoint.attributes.id)) {
            selectedLineCopy.attributes.topointid = newPoint.attributes.id;
            selectedLineCopy.geometry.paths[0][1][0] = newPoint.geometry.x;
            selectedLineCopy.geometry.paths[0][1][1] = newPoint.geometry.y;
        } else if (parseInt(selectedLineCopy.attributes.topointid) === parseInt(selectedPoint.attributes.id)) {
            selectedLineCopy.attributes.frompointid = newPoint.attributes.id;
            selectedLineCopy.geometry.paths[0][0][0] = newPoint.geometry.x;
            selectedLineCopy.geometry.paths[0][0][1] = newPoint.geometry.y;
        }

        selectedLineCopy.geometry.clearCache();

        return this.featuresApplyUpdateService.updateFeaturesByType(this.spatialUtilService.getLineMapLayer(), [selectedLineCopy]).pipe(
            map((updatedLines: any[]) => {
                if (updatedLines) {
                    console.debug(`${logPrefix} - updated line count: ${updatedLines.length}.`);
                    return updatedLines[0];
                } else {
                    console.error(`${logPrefix} - error updating selected line.`);
                    return undefined;
                }
            })
        );
    }

    private updateRelatedPolylines(fromPointId: any, toPointId: any, selectedPointId: any, newPoint: any) {
        const logPrefix = `${this.title} - updateRelatedPolylines`;
        console.debug(logPrefix);

        if (fromPointId && toPointId && (selectedPointId === fromPointId || selectedPointId === toPointId)) {
            const updatedPolylines = this.lines
                .filter((line: any) => this.getLineTypeLookups().some(l => l.featureType === 'polyline' && line.attributes.type === l.id))
                .map((line: any) => {
                    const bracketedPointIds = (line.attributes.pointids && line.attributes.pointids.split(',')) || [];
                    const fromPointIdx = bracketedPointIds.indexOf(`[${fromPointId}]`);
                    const toPointIdx = bracketedPointIds.indexOf(`[${toPointId}]`);
                    return {graphic: line, bracketedPointIds: bracketedPointIds, fromPointIdx: fromPointIdx, toPointIdx: toPointIdx};
                })
                .filter((v: any) => v.fromPointIdx !== -1 && v.toPointIdx !== -1 && Math.abs(v.toPointIdx - v.fromPointIdx) === 1)
                .map((v: any) => {
                    const newPointIdx = Math.max(v.fromPointIdx, v.toPointIdx);
                    const updatedPointIds = v.bracketedPointIds.slice();
                    updatedPointIds.splice(newPointIdx, 0, `[${newPoint.attributes.id}]`);

                    const newPolyline = v.graphic.toJson();
                    newPolyline.attributes.pointids = updatedPointIds.join();
                    newPolyline.geometry.paths[0].splice(newPointIdx, 0, [newPoint.geometry.x, newPoint.geometry.y]);

                    return new this.esriModuleService.Graphic(newPolyline);
                });

            if (updatedPolylines.length) {
                return this.spatialUtilService.applyEdits('line', null, updatedPolylines, null).pipe(
                    tap((res: any) => console.debug(`${logPrefix} - applyEdits response:`, res)),
                    catchError((err: any) => {
                        console.error(`${logPrefix} - applyEdits error:`, err);
                        return err;
                    })
                );
            }
        }

        return of(null);
    }

    private createDissectedLine(lineBeforeUpdate: any, selectedPoint: any, newPoint: any) {
        const logPrefix = `${this.title} - createDissectedLine`;
        console.debug(logPrefix);

        const dissectedLine = new this.esriModuleService.Graphic(lineBeforeUpdate.toJson());

        this.spatialUtilService.deleteAttrs(dissectedLine.attributes, ['id']);

        const fromPoint = new this.esriModuleService.geometry.Point(dissectedLine.geometry.paths[0][0]);
        fromPoint.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        const toPoint = new this.esriModuleService.geometry.Point(dissectedLine.geometry.paths[0][1]);
        toPoint.spatialReference = new this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference());

        dissectedLine.attributes.distance = parseFloat(Math.abs(dissectedLine.attributes.distance - this.attributes.distance).toFixed(3));

        const selectedPointId = selectedPoint.attributes.id;
        const fromPointId = dissectedLine.attributes.frompointid;
        const toPointId = dissectedLine.attributes.topointid;

        if (parseInt(dissectedLine.attributes.frompointid) === parseInt(selectedPoint.attributes.id)) {
            dissectedLine.attributes.frompointid = newPoint.attributes.id;
            dissectedLine.geometry.paths[0][0][0] = newPoint.geometry.x;
            dissectedLine.geometry.paths[0][0][1] = newPoint.geometry.y;
        } else if (parseInt(dissectedLine.attributes.topointid) === parseInt(selectedPoint.attributes.id)) {
            dissectedLine.attributes.topointid = newPoint.attributes.id;
            dissectedLine.geometry.paths[0][1][0] = newPoint.geometry.x;
            dissectedLine.geometry.paths[0][1][1] = newPoint.geometry.y;
        }

        dissectedLine.geometry.clearCache();

        return this.lineService.commitLineFeatureCreation(dissectedLine).pipe(
            switchMap((insertedFeatures: any[]) => {
                if (insertedFeatures) {
                    console.debug(`${logPrefix} - dissected line count: ${insertedFeatures.length}.`);
                    return this.updateRelatedPolylines(fromPointId, toPointId, selectedPointId, newPoint).pipe(map(() => insertedFeatures));
                } else {
                    console.error(`${logPrefix} - error creating dissected line.`);
                    return of(undefined);
                }
            })
        );
    }

    private getSelectedLineAngles(selectedLine: any) {
        console.debug(`${this.title} - getSelectedLineAngles`);

        const fromPointAngles = this.allAngles.filter(
            e =>
                Number(e.attributes.pointid) === Number(selectedLine.attributes.frompointid) &&
                (Number(e.attributes.anglefromlineid) === Number(selectedLine.attributes.id) ||
                    Number(e.attributes.angletolineid) === Number(selectedLine.attributes.id))
        );

        const toPointAngles = this.allAngles.filter(
            e =>
                Number(e.attributes.pointid) === Number(selectedLine.attributes.topointid) &&
                (Number(e.attributes.anglefromlineid) === Number(selectedLine.attributes.id) ||
                    Number(e.attributes.angletolineid) === Number(selectedLine.attributes.id))
        );

        return [...fromPointAngles, ...toPointAngles];
    }

    private updateSelectedLineAngles(selectedLineAngles: any[], selectedPoint: any, lineBeforeUpdate: any, newLine: any) {
        const logPrefix = `${this.title} - updateSelectedLineAngles`;
        console.debug(logPrefix);

        const anglesToBeUpdated = [];
        const nextLinesForSelectedLine = this.getNextLinesForSelectedLine(selectedPoint, lineBeforeUpdate, newLine);

        if (nextLinesForSelectedLine && nextLinesForSelectedLine.length > 0) {
            selectedLineAngles.forEach((angle: any) => {
                nextLinesForSelectedLine.forEach((nextLine: any) => {
                    if (angle.attributes.anglefromlineid === nextLine.attributes.id) {
                        const angleCopy = new this.esriModuleService.Graphic(angle.toJson());
                        angleCopy.attributes.angletolineid = newLine.attributes.id;
                        anglesToBeUpdated.push(angleCopy);
                    } else if (angle.attributes.angletolineid === nextLine.attributes.id) {
                        const angleCopy = new this.esriModuleService.Graphic(angle.toJson());
                        angleCopy.attributes.anglefromlineid = newLine.attributes.id;
                        anglesToBeUpdated.push(angleCopy);
                    }
                });
            });

            if (anglesToBeUpdated.length > 0) {
                return this.spatialUtilService.applyEdits('angle', null, anglesToBeUpdated, null).pipe(
                    map((res: any) => {
                        if (res.updates) {
                            console.debug(`${logPrefix} - ${res.updates.length} selected line angles have been updated.`);
                            return res.updates;
                        } else {
                            console.error(`${logPrefix} - error updating selected line angles.`);
                            return undefined;
                        }
                    })
                );
            } else {
                return of([]);
            }
        } else {
            return of([]);
        }
    }

    private getNextLinesForSelectedLine(selectedPoint: any, selectedLine: any, newLine: any) {
        console.debug(`${this.title} - getNextLinesForSelectedLine`);

        let secondLinePoint = undefined;
        if (parseInt(selectedLine.attributes.frompointid) === parseInt(selectedPoint.attributes.id)) {
            secondLinePoint = parseInt(selectedLine.attributes.topointid);
        } else {
            secondLinePoint = parseInt(selectedLine.attributes.frompointid);
        }

        return this.lines.filter(
            l =>
                selectedLine.attributes.id !== l.attributes.id &&
                newLine.attributes.id !== l.attributes.id &&
                (l.attributes.frompointid === secondLinePoint || l.attributes.topointid === secondLinePoint)
        );
    }

    private createDissectedLineAngles(newPoint: any, selectedLine: any, newLine: any) {
        console.debug(`${this.title} - createDissectedLineAngles`);

        const generatedCadastralAngles = this.angleCreationService.createMissingCadastralAngles(
            this.allAngles,
            [newPoint],
            [selectedLine, newLine],
            this.angleCreationService.ANGLE_GENERATION_METHOD.CONSTRUCTION_TOOLS,
            undefined
        );

        if (generatedCadastralAngles.length > 0) {
            generatedCadastralAngles.forEach((angle: any) => {
                angle.attributes.internalangle = '180:00:00';
                angle.attributes.visible = 'NO';
            });

            return this.angleCreationService.insertAngles(generatedCadastralAngles);
        } else {
            // generate interest angles
            const generatedInterestAngles = this.angleCreationService.createMissingCadastralAngles(
                this.allAngles,
                [newPoint],
                [selectedLine, newLine],
                this.angleCreationService.ANGLE_GENERATION_METHOD.CONSTRUCTION_TOOLS,
                true
            );

            if (generatedInterestAngles.length > 0) {
                generatedInterestAngles.forEach((angle: any) => {
                    angle.attributes.internalangle = '180:00:00';
                    angle.attributes.visible = 'NO';
                });

                return this.angleCreationService.insertAngles(generatedInterestAngles);
            } else {
                return of([]);
            }
        }
    }

    private updateAssociatedPolygons(lineBeforeUpdate: any, newPoint: any, newLine: any) {
        const logPrefix = `${this.title} - updateAssociatedPolygons`;
        console.debug(logPrefix);

        const associatedPolygonsBeforeUpdate = [];
        const associatedPolygonsToBeUpdated = [];

        const filteredPolygons = this.polygons.filter(
            p => p.attributes.lineids && p.attributes.lineids.includes(`[${lineBeforeUpdate.attributes.id}]`)
        );

        for (const polygon of filteredPolygons) {
            associatedPolygonsBeforeUpdate.push(polygon);

            const polygonBeforeUpdate = new this.esriModuleService.Graphic(polygon.toJson());

            const ringBeforeUpdate = polygonBeforeUpdate.geometry.rings[0];
            const ringPoints = [];

            const tolerance = newPoint.geometry.x <= 180 && newPoint.geometry.y <= 90 ? 0.000001 : 0.1;

            // Assuming we do not have multipart polygon
            ringBeforeUpdate.forEach((ringPoint: any, i: number, a: any[]) => {
                ringPoints.push(ringPoint);

                if (i < a.length - 1) {
                    const nextRingPoint = a[i + 1];

                    const ringX = ringPoint[0];
                    const ringY = ringPoint[1];
                    const nextRingX = nextRingPoint[0];
                    const nextRingY = nextRingPoint[1];

                    const pathBeforeUpdate = lineBeforeUpdate.geometry.paths[0];
                    const x0 = pathBeforeUpdate[0][0];
                    const y0 = pathBeforeUpdate[0][1];
                    const x1 = pathBeforeUpdate[1][0];
                    const y1 = pathBeforeUpdate[1][1];

                    if (
                        (this.spatialUtilService.tolEquals(ringX, x0, tolerance) &&
                            this.spatialUtilService.tolEquals(ringY, y0, tolerance) &&
                            this.spatialUtilService.tolEquals(nextRingX, x1, tolerance) &&
                            this.spatialUtilService.tolEquals(nextRingY, y1, tolerance)) ||
                        (this.spatialUtilService.tolEquals(ringX, x1, tolerance) &&
                            this.spatialUtilService.tolEquals(ringY, y1, tolerance) &&
                            this.spatialUtilService.tolEquals(nextRingX, x0, tolerance) &&
                            this.spatialUtilService.tolEquals(nextRingY, y0, tolerance))
                    ) {
                        ringPoints.push([newPoint.geometry.x, newPoint.geometry.y]);
                    }
                }
            });

            if (ringBeforeUpdate.length !== ringPoints.length) {
                const polygonGeometry = new this.esriModuleService.geometry.Polygon();
                polygonGeometry.setSpatialReference(polygonBeforeUpdate.geometry.spatialReference);
                polygonGeometry.addRing(ringPoints);
                polygonBeforeUpdate.geometry = polygonGeometry;

                polygonBeforeUpdate.attributes.lineids = this.getUpdatedPolygonLineIdsAttribute(polygonBeforeUpdate, lineBeforeUpdate, newLine);

                associatedPolygonsToBeUpdated.push(polygonBeforeUpdate);
            } else {
                console.error(`${logPrefix} - error matching line geometry to an associated polygon.`);
                return of(undefined);
            }
        }

        if (associatedPolygonsToBeUpdated.length > 0) {
            return this.featuresApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getPolygonMapLayer(), associatedPolygonsToBeUpdated)
                .pipe(
                    map((updatedFeatures: any[]) => {
                        if (updatedFeatures) {
                            console.debug(`${logPrefix} - ${updatedFeatures.length} polygons have been updated.`);
                            return updatedFeatures;
                        } else {
                            console.error(`${logPrefix} - error updating polygons.`);
                            return associatedPolygonsBeforeUpdate;
                        }
                    })
                );
        } else {
            return of([]);
        }
    }

    private getUpdatedPolygonLineIdsAttribute(polygon: any, lineBeforeUpdate: any, newLine: any) {
        console.debug(`${this.title} - getUpdatedPolygonLineIdsAttribute`);

        const updatedLineIds = [];

        const lineIds = polygon.attributes.lineids.replace(/\[/g, '').replace(/]/g, '').split(',');

        const lineFeatures = lineIds.map((lineId: any) => this.lines.find(l => Number(lineId) === Number(l.attributes.id)));

        lineIds.forEach((lineId: any, index: number) => {
            if (Number(lineId) === Number(lineBeforeUpdate.attributes.id)) {
                if (index === 0) {
                    // adjacent line feature is the next line feature in the list
                    const nextLineFeature = lineFeatures.length > index ? lineFeatures[index + 1] : undefined;
                    const orderedLineIds = this.orderLineIds(nextLineFeature, lineBeforeUpdate, newLine);

                    // add the line ids in reverse order
                    updatedLineIds.push(orderedLineIds[1]);
                    updatedLineIds.push(orderedLineIds[0]);
                } else {
                    // adjacent line feature is the preceding line feature in the list
                    const previousLineFeature = lineFeatures[index - 1];
                    updatedLineIds.push(...this.orderLineIds(previousLineFeature, lineBeforeUpdate, newLine));
                }
            } else {
                updatedLineIds.push(`[${lineId}]`);
            }
        });

        return updatedLineIds.join();
    }

    /**
     * Returns the ordered lines. The adjacent feature is used to compare the frompointid and topointid and orders the lines accordingly.
     */
    private orderLineIds(adjacentLineFeature: any, lineBeforeUpdate: any, newLine: any) {
        console.debug(`${this.title} - orderLineIds`);

        if (!adjacentLineFeature) {
            return [this.formatLineId(lineBeforeUpdate), this.formatLineId(newLine)];
        }

        if (
            (Number(lineBeforeUpdate.attributes.frompointid) === Number(adjacentLineFeature.attributes.frompointid) ||
                Number(lineBeforeUpdate.attributes.frompointid) === Number(adjacentLineFeature.attributes.topointid)) &&
            Number(lineBeforeUpdate.attributes.frompointid) === Number(newLine.attributes.frompointid)
        ) {
            // new line is connected to the adjacent line via frompointid, return the new line followed by the original line
            return [this.formatLineId(newLine), this.formatLineId(lineBeforeUpdate)];
        } else if (
            (Number(lineBeforeUpdate.attributes.topointid) === Number(adjacentLineFeature.attributes.frompointid) ||
                Number(lineBeforeUpdate.attributes.topointid) === Number(adjacentLineFeature.attributes.topointid)) &&
            Number(lineBeforeUpdate.attributes.topointid) === Number(newLine.attributes.topointid)
        ) {
            // new line is connected to the adjacent line via topointid, return the new line followed by the original line
            return [this.formatLineId(newLine), this.formatLineId(lineBeforeUpdate)];
        } else {
            // return default order
            return [this.formatLineId(lineBeforeUpdate), this.formatLineId(newLine)];
        }
    }

    private formatLineId(line: any) {
        return `[${line.attributes.id}]`;
    }

    private selectedLineRollback(newPoint: any, selectedLine: any) {
        if (selectedLine) {
            return this.featuresApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getLineMapLayer(), [selectedLine])
                .pipe(tap(() => this.featuresDeletionService.deleteFeatures([newPoint], this.spatialUtilService.getPointMapLayer())));
        } else {
            return this.featuresDeletionService.deleteFeatures([newPoint], this.spatialUtilService.getPointMapLayer());
        }
    }

    private rollback(newPoint: any, selectedLine: any, updatedAngles: any[]) {
        console.debug(`${this.title} - rollback`);

        if (updatedAngles) {
            return this.featuresApplyUpdateService
                .updateFeaturesByType(this.spatialUtilService.getAngleMapLayer(), updatedAngles)
                .pipe(switchMap(() => this.selectedLineRollback(newPoint, selectedLine)));
        } else {
            return this.selectedLineRollback(newPoint, selectedLine);
        }
    }
}
